import { useStore } from '@tanstack/react-form';
import PropTypes from 'prop-types';
import { use, useCallback, useEffect, useId, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getMediaUrl } from '../../../lib/flotiq-client/api-helpers';
import FlotiqPlugins from '../../../lib/flotiq-plugins/flotiqPluginsRegistry';
import { FormRelationAfterSubmitEvent } from '../../../lib/flotiq-plugins/plugin-events/FormRelationAfterSubmitEvent';
import { EMPTY_FUNCTION, EMPTY_OBJECT } from '../../../lib/globals';
import { getTestProps } from '../../../lib/helpers';
import { RolePermissions } from '../../../lib/rolePermissions';

import Button from '../../../components/Button/Button';
import CreateObjectContentModal from '../../../components/CreateObjectContentModal/CreateObjectContentModal';
import EditObjectContentModal from '../../../components/EditObjectContentModal/EditObjectContentModal';
import FileButton from '../../../components/FileButton/FileButton';
import HelpErrorTextsTemplate from '../../../components/HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import LinkObjectContentModal from '../../../components/LinkObjectContentModal/LinkObjectContentModal';
import PickTypeContentModal from '../../../components/PickTypeContentModal/PickTypeContentModal';
import DraggableRelationCard from '../../../components/RelationCard/DraggableRelationCard/DraggableRelationCard';
import RequiredTemplate from '../../../components/RequiredTemplate/RequiredTemplate';
import Tooltip from '../../../components/Tooltip/Tooltip';

import { useFeaturedImages } from '../../../hooks/api/useFeaturedImages';
import useMediaUpload from '../../../hooks/api/useMediaUpload';
import { useRelations } from '../../../hooks/api/useRelations';
import useDebounceCallback from '../../../hooks/useDebounceCallback';

import ContentObjectFormContext from '../../../contexts/ContentObjectFormContext';
import { defaultButtonsProps, useModals } from '../../../contexts/ModalContext';
import UserContext from '../../../contexts/UserContext';

import { useFieldContext, useFormContext } from '../../form';
import { useFieldErrors } from '../../useFieldErrors';

const getObjectData = (dataUrl) => {
  const split = dataUrl.split('/');
  return [split[split.length - 2], split[split.length - 1]];
};

const RelationField = ({
  label = '',
  required = false,
  validation = EMPTY_OBJECT,
  helpText = '',
  disabled = false,
  readonly = false,
  onMediaUpload = EMPTY_FUNCTION,
  additionalClasses = '',
  testId = '',
}) => {
  const form = useFormContext();
  const field = useFieldContext();

  const { t } = useTranslation();
  const modal = useModals();
  const [isUploading, setIsUploading] = useState(false);
  const { removeNewMedia } = useMediaUpload();
  const { onVariantCreate, onVariantDelete, contentType, initialData } = use(
    ContentObjectFormContext,
  );
  const { permissions } = use(UserContext);

  const error = useFieldErrors();

  const actionsDisabled = disabled || readonly;

  const relationType = validation?.relationContenttype;
  const hasOnlyMedia = relationType === '_media';
  const isMultiple = validation?.relationMultiple;

  const { canCreate: canCreateRelation, canRead: canReadRelation } = useMemo(
    () =>
      !relationType
        ? { canCreate: true, canRead: true }
        : permissions.getCoPermissions(
            relationType,
            RolePermissions.PERMISSIONS_TYPES.READ,
          ),
    [permissions, relationType],
  );

  const value = useMemo(() => field.state.value || [], [field.state.value]);

  const onBlur = useCallback(() => field.handleBlur(), [field]);

  const handleUpload = useCallback(
    async (files) => {
      setIsUploading(true);
      const newMedia = await onMediaUpload(files);
      const newRelations = [];

      newMedia.forEach(({ media, state }) => {
        if (!isMultiple && newRelations.length > 0) return;
        if (state === 'uploaded')
          newRelations.push({
            type: 'internal',
            dataUrl: `/api/v1/content/_media/${media.id}`,
          });
      });

      if (newRelations.length > 0) {
        field.setValue(Object.values([...value, ...newRelations]));
      }
      setIsUploading(false);
    },
    [field, isMultiple, onMediaUpload, value],
  );

  const openRelationModal = useCallback(
    async (_, idx = null) => {
      const selected =
        idx === null
          ? value.reduce((selected, relation) => {
              const split = relation.dataUrl.split('/');
              const id = split[split.length - 1];
              selected[id] = relation;
              return selected;
            }, {})
          : {};
      const isLocallyMultiple = isMultiple && idx === null;
      const oldValue = [...value];

      const newRelations = await modal({
        title: (
          <div className="text-xl md:text-3xl">
            {t('ContentForm.Relation.PickObjects', { label })}
          </div>
        ),
        content: (
          <LinkObjectContentModal
            relationType={relationType}
            selected={selected}
            isMultiple={isLocallyMultiple}
            onMediaUpload={onMediaUpload}
            {...getTestProps(testId, 'link-modal', 'testId')}
          />
        ),
        size: '2xl',
        dialogAdditionalClasses: 'max-h-[calc(100vh-32px)]',
      });

      if (newRelations) {
        if (idx !== null) {
          const newRelation = Object.values(newRelations)[0];
          const newDatUrl = newRelation.dataUrl;
          if (oldValue.find((obj) => obj.dataUrl === newDatUrl)) {
            toast.error(t('ContentForm.Relation.AlreadyInArray'));
          } else {
            oldValue[idx] = newRelation;
            field.setValue(Object.values(oldValue));
          }
        } else {
          field.setValue(Object.values(newRelations));
        }
      }

      setTimeout(onBlur);
    },
    [
      value,
      modal,
      t,
      label,
      relationType,
      isMultiple,
      onMediaUpload,
      testId,
      field,
      onBlur,
    ],
  );

  const openCreateObjectModal = useCallback(async () => {
    const pickedType = await modal({
      title: (
        <div className="text-xl md:text-3xl">
          {t('ContentForm.Relation.PickType')}
        </div>
      ),
      content: (
        <PickTypeContentModal
          relationType={relationType}
          {...getTestProps(testId, 'pick', 'testId')}
        />
      ),
      size: '2xl',
      buttons: [{ ...defaultButtonsProps.cancel, label: t('Global.Cancel') }],
    });

    if (!pickedType) return;

    const objectData = await modal({
      content: (
        <CreateObjectContentModal
          contentTypeName={pickedType}
          {...getTestProps(testId, 'create', 'testId')}
        />
      ),
      size: 'page',
      hideClose: true,
    });

    if (objectData) {
      field.pushValue({
        type: 'internal',
        dataUrl: `/api/v1/content/${pickedType}/${objectData.id}`,
      });
    }
    setTimeout(onBlur);
    removeNewMedia();
  }, [modal, t, relationType, testId, onBlur, removeNewMedia, field]);

  const defaultParams = useMemo(
    () => ({ page: 1, limit: value.length }),
    [value.length],
  );

  const {
    data: relationsDict,
    isLoading: relationsAreLoading,
    reload: reloadRelations,
  } = useRelations(value, defaultParams);

  const contentTypes = useMemo(
    () =>
      relationsAreLoading || !relationsDict
        ? []
        : Object.values(relationsDict).map(({ contentType }) => contentType),
    [relationsAreLoading, relationsDict],
  );

  const [featuredImages, featuredImagesLoading] =
    useFeaturedImages(contentTypes);

  const { values } = useStore(form.store, (state) => ({
    values: state.values,
  }));

  const modalId = useId();

  const openEditModal = useCallback(
    async (ctdName, id, val, fieldName) => {
      await modal({
        id: modalId,
        content: (
          <EditObjectContentModal
            contentTypeName={ctdName}
            id={id}
            onSubmit={({
              success,
              contentObject: relationContentObject,
              contentType: relationContentType,
              errors,
            }) => {
              FlotiqPlugins.run(
                'flotiq.form.relation::after-submit',
                new FormRelationAfterSubmitEvent({
                  contentType: contentType,
                  contentObject: initialData,
                  fieldName,
                  relation: val,
                  relationContentObject,
                  relationContentType,
                  isSuccess: success,
                  errros: errors,
                  values,
                }),
              );
            }}
            {...getTestProps(testId, 'edit', 'testId')}
          />
        ),
        size: 'page',
        hideClose: true,
      });

      reloadRelations();
    },
    [contentType, initialData, modal, modalId, reloadRelations, testId, values],
  );

  useEffect(() => {
    return () => {
      modal.close(modalId);
    };
  }, [modal, modalId]);

  const limitReached = useMemo(
    () => !isMultiple && value.length > 0,
    [isMultiple, value],
  );

  const deleteRelation = useCallback(
    async (idx) => {
      const result = await modal.delete(t('ContentForm.RemoveItem'));
      if (!result) return;
      field.removeValue(idx);
      onBlur();
    },
    [field, modal, onBlur, t],
  );

  const swapRelation = useCallback(
    async (idx) => {
      openRelationModal(null, idx);
    },
    [openRelationModal],
  );

  const globalError = useMemo(() => {
    if (!error) return;
    const parsedError = Array.isArray(error) ? error[0] : error;
    if (typeof parsedError === 'string') return error;
  }, [error]);

  const openFullSizeImage = useCallback((media) => {
    window.open(getMediaUrl(media), '_blank');
  }, []);

  const handleAddVariant = useCallback(
    async (media) => {
      const [data, hasErrors] = await onVariantCreate(media);
      if (!hasErrors && data) reloadRelations();
    },
    [onVariantCreate, reloadRelations],
  );

  const handleDeleteVariant = useCallback(
    async (media, idx) => {
      modal.deleting('variant-delete');

      const [data, hasErrors] = await onVariantDelete(media, idx);
      if (!hasErrors && data) reloadRelations();
    },
    [modal, onVariantDelete, reloadRelations],
  );

  const onDeleteVariant = useCallback(
    async (media, idx) => {
      await modal.delete(
        t('ContentForm.VariantDeleteConfirm'),
        'variant-delete',
        () => handleDeleteVariant(media, idx),
      );
    },
    [handleDeleteVariant, modal, t],
  );

  const handleChangeOrder = useCallback(
    (sourceDataUrl, targetDataUrl) => {
      if (sourceDataUrl === targetDataUrl) return;

      const sourceIndex = value.findIndex(
        ({ dataUrl }) => dataUrl === sourceDataUrl,
      );
      const targetIndex = value.findIndex(
        ({ dataUrl }) => dataUrl === targetDataUrl,
      );

      if (sourceIndex >= 0 && targetIndex >= 0) {
        field.moveValue(sourceIndex, targetIndex);
        onBlur();
      }
    },
    [field, onBlur, value],
  );

  const onDrop = useDebounceCallback(handleChangeOrder, 10);

  return (
    <div className={twMerge('relative', additionalClasses)}>
      <label
        className="block text-sm text-slate-400 dark:text-gray-200 mb-1"
        {...getTestProps(testId, 'label')}
      >
        {label}
        {required && <RequiredTemplate />}
      </label>
      {!limitReached && (
        <div
          className="flex flex-wrap gap-2 mb-2"
          {...getTestProps(testId, 'buttons-container')}
        >
          <>
            <Tooltip
              tooltip={
                canCreateRelation ? '' : t('ContentForm.Relation.CannotCreate')
              }
            >
              {hasOnlyMedia ? (
                <FileButton
                  onUpload={handleUpload}
                  multiple={isMultiple}
                  disabled={actionsDisabled || !canCreateRelation}
                  label={t('ContentForm.Relation.AddMedia')}
                  onBlur={onBlur}
                  additionalClasses="w-fit"
                  testId={testId}
                />
              ) : (
                <Button
                  onClick={openCreateObjectModal}
                  buttonSize="xs"
                  disabled={actionsDisabled || !canCreateRelation}
                  {...getTestProps(testId, 'add-object', 'testId')}
                >
                  {t('ContentForm.Relation.AddObject', {
                    label,
                  })}
                </Button>
              )}
            </Tooltip>
            <Tooltip
              tooltip={
                canReadRelation ? '' : t('ContentForm.Relation.CannotRead')
              }
            >
              {hasOnlyMedia ? (
                <Button
                  onClick={openRelationModal}
                  buttonColor="blueBordered"
                  buttonSize="xs"
                  onBlur={onBlur}
                  disabled={isUploading || actionsDisabled || !canReadRelation}
                  {...getTestProps(testId, 'link-media', 'testId')}
                >
                  {t('ContentForm.Relation.LinkMedia')}
                </Button>
              ) : (
                <Button
                  onClick={openRelationModal}
                  buttonColor="blueBordered"
                  buttonSize="xs"
                  onBlur={onBlur}
                  disabled={actionsDisabled || !canReadRelation}
                  {...getTestProps(testId, 'link-object', 'testId')}
                >
                  {t('ContentForm.Relation.PickObjects', {
                    label,
                  })}
                </Button>
              )}
            </Tooltip>
          </>
        </div>
      )}
      {value.length > 0 && (
        <div
          className="flex flex-col gap-2 mb-2"
          {...getTestProps(testId, 'relations-container')}
        >
          <div className={twMerge('flex flex-col space-y-4')}>
            {relationsDict &&
              value.map((val, idx, currentValue) => {
                const [ctdName, id] = getObjectData(val.dataUrl);
                const relations = relationsAreLoading
                  ? relationsDict[ctdName]
                  : relationsDict[ctdName]?.data;
                const relation = (relations || []).find((element) =>
                  relationsAreLoading ? element === id : element.id === id,
                );

                const relationToShow = relation ? relation : { id };
                return (
                  <div
                    key={
                      typeof relationToShow === 'string'
                        ? relationToShow
                        : relationToShow.id
                    }
                  >
                    <form.Subscribe
                      selector={(state) =>
                        state.errors[0]?.[`${field.name}[${idx}].dataUrl`]
                      }
                      children={(itemError) => (
                        <>
                          <DraggableRelationCard
                            onDrop={onDrop}
                            dataUrl={val.dataUrl}
                            relation={relationToShow}
                            relationType={ctdName}
                            titleField={relationsDict[ctdName]?.titleField}
                            featuredImageUrl={featuredImages[ctdName]}
                            featuredImageLoading={featuredImagesLoading}
                            relationsAreLoading={relationsAreLoading}
                            relationsLength={currentValue.length}
                            onDelete={() => deleteRelation(idx)}
                            onEdit={
                              !relation
                                ? undefined
                                : () =>
                                    openEditModal(
                                      ctdName,
                                      id,
                                      val,
                                      `${field.name}[${idx}]`,
                                    )
                            }
                            onSwap={() => swapRelation(idx)}
                            onShow={openFullSizeImage}
                            disabled={actionsDisabled}
                            onVariantCreate={
                              !relation ? null : handleAddVariant
                            }
                            onVariantDelete={!relation ? null : onDeleteVariant}
                            additionalContainerClasses={
                              itemError || !relation ? 'border border-red' : ''
                            }
                            withObjectStatus
                            testId={testId}
                          />
                          <HelpErrorTextsTemplate
                            error={
                              typeof itemError === 'string'
                                ? itemError
                                : itemError?.map((er) => er.message)
                            }
                            {...getTestProps(
                              testId,
                              `${ctdName}-${id}`,
                              'testId',
                            )}
                          />
                        </>
                      )}
                    />
                  </div>
                );
              })}
          </div>
        </div>
      )}
      <HelpErrorTextsTemplate
        helpText={helpText}
        error={globalError}
        testId={testId}
      />
    </div>
  );
};

export default RelationField;

RelationField.propTypes = {
  /**
   * Array helpers from formik array field
   */
  arrayHelpers: PropTypes.object.isRequired,
  /**
   * Relation field label above relations and buttons
   */
  label: PropTypes.node,
  /**
   * If relation field is required
   */
  required: PropTypes.bool,
  /**
   * Validation relations object
   */
  validation: PropTypes.object,
  /**
   * Error text under relations and buttons
   */
  error: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    ),
    PropTypes.string,
  ]),
  /**
   * Help text under relations and buttons
   */
  helpText: PropTypes.node,
  /**
   * If field is disabled
   */
  disabled: PropTypes.bool,
  /**
   * If field is readonly (non-interactive but without disabled classes)
   */
  readonly: PropTypes.bool,
  /**
   * On media upload callback
   */
  onMediaUpload: PropTypes.func,
  /**
   * Additional container classes
   */
  additionalClasses: PropTypes.string,
  /**
   * Test id for field
   */
  testId: PropTypes.string,
};
